/**
 * The event framework's replicated object family.
 *
 * Link identity, issuer authorization, and quantity stay orthogonal here. Only **two** kinds of object are ever
 * replicated, and the event layer stays domain-agnostic (it never names Post, ActorRoute, Reply, etc.):
 *
 * - [SignedCommitment] — the *only* signed object: an actor's attestation of one commitment-chain head.
 * - [LinkPreimage] — unsigned, content-addressed structure that any node can verify on its own.
 *
 * Everything an application means by "amount", "target", "body", or "credential material" is just Link structure, so
 * it travels as [LinkPreimage]s. Withholding a preimage is therefore selective reveal: the head stays attributable
 * while the withheld body shows up as [com.oasisfeng.invaluable.data.Observation.Missing] for everyone else.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.actor.Commitment
import com.oasisfeng.invaluable.actor.CommitmentContent
import com.oasisfeng.invaluable.actor.Credential
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.data.projectedRecords
import com.oasisfeng.invaluable.identity.Identity
import kotlinx.io.bytestring.ByteString

/** A single replicated object in the event framework. */
sealed interface ReplicatedEvent

/**
 * An actor's signed attestation that [head] is a commitment in its own append-only log.
 *
 * The signature is produced by the issuer's [credential] key over [canonicalBytes]. Because a commitment head's
 * projection content-addresses its whole ancestry (up to `actor -> credential`), one head signature transitively
 * authenticates the entire chain it terminates. The framework's MVP policy publishes one attestation per appended
 * commitment, so every commitment becomes independently attributable and forks (two heads sharing a parent under one
 * credential) remain observable facts rather than something the event layer arbitrates.
 *
 * Both endpoints are carried as projections, not inlined structure: the verifier resolves [credential] (and attributes
 * [head] to that credential's actor) from replicated [LinkPreimage]s. A withheld body never blocks this attribution,
 * which is what lets the header stay visible while its content is a missing observation.
 *
 * @property head Projection of the attested commitment head Link.
 * @property credential Projection of the issuer's credential Link.
 * @property signature Detached credential-protocol signature over [canonicalBytes].
 */
data class SignedCommitment(
    val head: LinkProjection,
    val credential: LinkProjection,
    val signature: ByteString,
) : ReplicatedEvent {

    /** Deterministic bytes bound by the issuer's credential signature. */
    fun canonicalBytes(): ByteArray = signedCommitmentCanonicalBytes(head)

    companion object {
        /**
         * Sign [head] with [identity], deriving the issuer credential from the commitment's own actor.
         *
         * The identity must own the issuer credential's key; otherwise the resulting attestation could never verify.
         */
        fun sign(head: Commitment, identity: Identity, projector: LinkProjector): SignedCommitment {
            val credential = Credential.render(head.issuer.link.requireNestedTarget())
                ?: error("Commitment issuer is not founded on a credential.")
            require(Ed25519Credential.owns(credential, identity))
                { "Signing identity does not own the commitment issuer's credential key." }
            val headProjection = projector.project(head.link)
            val credentialProjection = projector.project(credential.link)
            val proof = Ed25519Credential.sign(identity, signedCommitmentCanonicalBytes(headProjection))
            return SignedCommitment(headProjection, credentialProjection, proof.payload)
        }
    }
}

/**
 * The wire form of a [ProjectedLinkRecord]: one content-addressed structural fact.
 *
 * It is unsigned because it is self-verifying — a receiver recomputes `projectLink(parent, target)` and checks it
 * equals [projection]. Null parent/target endpoints are the globally known kernel root, not missing preimages.
 */
data class LinkPreimage(val record: ProjectedLinkRecord) : ReplicatedEvent {
    val projection: LinkProjection get() = record.projection
}

/**
 * A locally produced commitment ready to replicate: its [attestation] plus the [LinkPreimage]s needed to resolve it,
 * split into two disclosure classes (Milestone 9):
 *
 * - [header] — the spine closure (actor/credential, previous-commitment chain, and the content-digest leaf). These
 *   attribute and order the commitment; they are always published.
 * - [content] — the committed [CommitmentContent] closure (target/body + quantity). **Selective reveal = publish
 *   [header] (and [attestation]) while withholding [content].** A withheld content is durably a missing observation:
 *   because the spine references content only by digest, successors stay attributable and accountable.
 */
data class PublishedCommitment(
    val attestation: SignedCommitment,
    val header: List<LinkPreimage>,
    val content: List<LinkPreimage>,
) {
    /** Every preimage (header then content), deduplicated by projection — the two closures share structural records. */
    val preimages: List<LinkPreimage> get() = (header + content).distinctBy { it.projection }
    /** Full reveal: preimages first, attestation last, so a sequential applier resolves dependencies before the head. */
    fun events(): List<ReplicatedEvent> = preimages + attestation
    /** Selective reveal: header (+ digest leaf) and attestation only — the committed content stays dark. */
    fun darkEvents(): List<ReplicatedEvent> = header + attestation
}

/**
 * Build the [PublishedCommitment] for a local commitment [head], splitting spine (header) from committed content.
 *
 * [alreadyPublished], when given, is a walk memo the caller carries across publishes: the closures below are then
 * emitted only as far as they are new to it. An actor appending to its own log publishes a head whose Link contains
 * every head before it, so without this the k-th publish re-walks the whole chain to produce a constant amount of new
 * records — quadratic over a log. It is the caller's job to have actually applied and shipped what a previous publish
 * returned, since anything the memo remembers will not be offered again; a caller that drops events on the floor must
 * not share a memo with one that does not. Passing nothing keeps each publish self-contained, header and content each
 * walking from empty exactly as before.
 */
fun publishCommitment(
    head: Commitment,
    identity: Identity,
    projector: LinkProjector,
    alreadyPublished: MutableMap<Link, LinkProjection>? = null,
): PublishedCommitment =
    PublishedCommitment(
        attestation = SignedCommitment.sign(head, identity, projector),
        header = projector.projectedRecords(head.link, alreadyPublished ?: HashMap()).map(::LinkPreimage),
        // The content node is referenced by the spine's digest only, so it is NOT reachable from head.link — gather it
        // from the commitment's known target/quantity so a full reveal can resolve it (and a dark publish can omit it).
        content = projector.projectedRecords(
            CommitmentContent(head.targetLink, head.quantity).link,
            // A fresh map per closure when unshared, so the two walks stay independent and `preimages` de-duplicates
            // their overlap exactly as it always has; one shared memo when carried, which subsumes that de-duplication.
            alreadyPublished ?: HashMap(),
        ).map(::LinkPreimage),
    )

private val SIGNED_COMMITMENT_DOMAIN = "invaluable/signed-commitment/v1\n".encodeToByteArray()

/**
 * Canonical signed bytes: a domain-separation tag followed by the head projection payload.
 *
 * MVP binds the projection scheme implicitly through the single configured projector. Encoding the scheme into these
 * bytes (to harden against cross-scheme replay) is deferred until more than one scheme is in use.
 */
internal fun signedCommitmentCanonicalBytes(head: LinkProjection): ByteArray =
    SIGNED_COMMITMENT_DOMAIN + head.payload.toByteArray()
